import { useAuthStore } from "../features/auth/store.ts";
import { reportClientError } from "./errorReporter.ts";
import { getIdToken } from "./firebase.ts";
import { getTenantSubdomain } from "./subdomain.ts";

// Empty/undefined env → root-relative paths (same-origin, behind Firebase Hosting).
// Otherwise use the explicit URL (e.g. http://localhost:8080 in dev).
const BASE =
  import.meta.env.VITE_API_BASE_URL === undefined
    ? "http://localhost:8080"
    : import.meta.env.VITE_API_BASE_URL;

export interface ApiErrorBody {
  code: string;
  message: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.status = status;
    this.code = body.code;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Don't auto-attach Firebase ID token */
  anonymous?: boolean;
}

/**
 * Build the auth/app headers attached to every authenticated request. Shared by
 * `api()` and the SSE streaming helper so both carry the same token logic
 * (EventSource can't set custom headers, hence fetch-based streaming).
 */
export async function buildAuthHeaders(
  init?: HeadersInit,
  anonymous = false,
  forceRefresh = false,
): Promise<Headers> {
  const headers = new Headers(init);
  if (anonymous) return headers;

  let token: string | null = null;
  try {
    token = await getIdToken(forceRefresh);
  } catch {
    // Firebase not initialized (dev mode with no project). MSW handlers
    // ignore Authorization, so just proceed without a token.
  }
  // In dev with MSW we still want to send *something* so the api side dev-token shortcut works
  // for any non-MSW curls; doesn't matter since MSW intercepts before fetch.
  if (token) headers.set("X-Authorization", `Bearer ${token}`);
  else if (import.meta.env.DEV) headers.set("X-Authorization", "Bearer dev-token");

  // NOTE: header must NOT start with "X-Google-"/"X-Goog-" — Google Front End /
  // IAP strips that reserved prefix, so the token never reaches the backend.
  const googleToken = useAuthStore.getState().googleAccessToken;
  if (googleToken && !headers.has("X-Drive-Token")) headers.set("X-Drive-Token", googleToken);

  const geminiKey = useAuthStore.getState().geminiKey;
  if (geminiKey) headers.set("X-Gemini-Key", geminiKey);

  const tenantSub = getTenantSubdomain();
  if (tenantSub && !headers.has("X-Tenant-Subdomain")) {
    headers.set("X-Tenant-Subdomain", tenantSub);
  }
  return headers;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, query, anonymous, headers: hdrs, ...rest } = opts;

  let url = `${BASE}${path}`;
  if (query) {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) search.set(k, String(v));
    }
    const s = search.toString();
    if (s) url += `?${s}`;
  }

  const bodyInit =
    body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body);
  const setContentType = (h: Headers) => {
    if (body !== undefined && !(body instanceof FormData))
      h.set("Content-Type", "application/json");
  };

  const headers = await buildAuthHeaders(hdrs, anonymous);
  setContentType(headers);

  let res: Response;
  try {
    res = await fetch(url, { ...rest, headers, body: bodyInit });
  } catch (err) {
    // Network failure (offline, DNS, CORS, aborted) — a real user-facing bug.
    void reportClientError({
      kind: "api",
      message: `Network error on ${path}: ${(err as Error).message}`,
      extra: { path },
    });
    throw err;
  }

  // A 401 on an authenticated request usually means the cached Firebase ID token
  // expired (e.g. after the laptop slept). Force-refresh the token and retry ONCE
  // before surfacing the error — otherwise the app would bounce the user to
  // sign-in even though their session is still valid ("keeps asking me to log in").
  if (res.status === 401 && !anonymous) {
    const retryHeaders = await buildAuthHeaders(hdrs, anonymous, true);
    setContentType(retryHeaders);
    if (retryHeaders.has("X-Authorization")) {
      res = await fetch(url, { ...rest, headers: retryHeaders, body: bodyInit });
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      // Non-JSON response (e.g. Cloud Run gateway 404 HTML page) — don't crash.
      json = { code: "non-json-response", message: text.slice(0, 200) };
    }
  }

  if (!res.ok) {
    const body =
      json && typeof json === "object"
        ? (json as ApiErrorBody)
        : { code: "unknown", message: `HTTP ${res.status}` };
    // Capture user-facing failures for the "User bugs" report. Previously only
    // 5xx were captured, so whole classes of real bugs that surface as a 4xx
    // (e.g. the "summary never generated" 404 loop) were invisible. Now capture
    // ALL 4xx + 5xx, suppressing only known non-bug noise:
    //   • 401 — the token expired and was auto-refreshed + retried above.
    //   • the summary page's expected "not ready yet" 404 while it polls
    //     /summary-data (that failure has its own explicit report on timeout).
    const isAuthRetry = res.status === 401;
    const isSummaryPoll = res.status === 404 && path.includes("/summary-data");
    const isInfographicEmpty = res.status === 422 && path.includes("/infographic");
    if (res.status >= 400 && !isAuthRetry && !isSummaryPoll && !isInfographicEmpty) {
      void reportClientError({
        kind: "api",
        message: `API ${res.status} on ${path}: ${body.message}`.slice(0, 300),
        extra: { path, status: String(res.status), code: body.code },
      });
    }
    throw new ApiError(res.status, body);
  }
  return json as T;
}

export interface SseHandlers<R = unknown> {
  /** A chunk of answer text. */
  onDelta?: (text: string) => void;
  /** The final references payload (sent once before [DONE]). */
  onReferences?: (refs: R) => void;
  signal?: AbortSignal;
}

/**
 * POST `body` and consume a `text/event-stream` response, invoking handlers per
 * `data:` event. Events are JSON: `{delta}`, `{references}`, or `{error}`; the
 * stream ends with `data: [DONE]`. Uses fetch (not EventSource) so it can send
 * the custom `X-Authorization` header.
 */
export async function streamSse<R = unknown>(
  path: string,
  body: unknown,
  handlers: SseHandlers<R> = {},
): Promise<void> {
  const headers = await buildAuthHeaders({
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  });
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal: handlers.signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    let errBody: ApiErrorBody = { code: "stream_failed", message: text || `HTTP ${res.status}` };
    try {
      const j = JSON.parse(text);
      if (j && typeof j === "object" && "message" in j) errBody = j as ApiErrorBody;
    } catch {
      // non-JSON error body — keep the raw text message
    }
    throw new ApiError(res.status, errBody);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // SSE events are separated by a blank line.
    let sep = buf.indexOf("\n\n");
    while (sep !== -1) {
      const event = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      sep = buf.indexOf("\n\n"); // advance now so `continue` can't loop forever
      const dataLine = event.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      const data = dataLine.slice(5).trim();
      if (!data) continue;
      if (data === "[DONE]") return;
      let obj: { delta?: unknown; references?: unknown; error?: unknown };
      try {
        obj = JSON.parse(data);
      } catch {
        continue; // ignore a malformed event
      }
      if (typeof obj.delta === "string") handlers.onDelta?.(obj.delta);
      else if (obj.references !== undefined) handlers.onReferences?.(obj.references as R);
      else if (typeof obj.error === "string")
        throw new ApiError(500, { code: "stream_error", message: obj.error });
    }
  }
}
